/**
 * Gán vai trò cho tài khoản - Step 3
 * Phân loại: Vai trò nghiệp vụ (CQQL/KHDN), Vai trò quản trị, Vai trò hệ thống
 */
import { FC, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Save, Undo2 } from 'lucide';

// MUI
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import Button from '@mui/material/Button';

// common
import Icon from 'common/components/Icon';

// screen
import {
  addAllClientsForNewGov,
  addAllOrRemoveAllClientsId,
  addClient,
  addNewGovRole,
  convertOrgTypeToOriginal,
  convertRoles,
  findByKey,
  removeClient,
  setCustomersByRoleType,
  shouldShowCustomerList,
  updateIncludedClientIds,
  updateSystemRoles,
} from 'modules/account/detail/helpers/utils';
import FilterButtons from 'modules/account/components/FilterButtons';
import type { Client, RolesToAssign, UtilFunctions } from 'modules/account/types';
import CustomersListHeading from 'modules/account/components/CustomersListHeading';
import RoleCategory from 'modules/account/components/RoleCategory';
import useGetAvailableRoles, {
  type CommonRole,
  formatRolesData,
  type Roles,
} from 'modules/account/services/useGetRoles';
import StaticRoleType from 'modules/account/components/roles/StaticRoleType';
import SystemAdminRoleType from 'modules/account/components/roles/SystemAdminRoleType';
import CustomersForCQQL from 'modules/account/components/customers/CustomersForCQQL';
import FullCustomersList from 'modules/account/components/customers/FullCustomersList';
import useGetHvsInfo from 'modules/account/services/useGetHvsInfo';
import useUserRoles from 'common/hook/useUserRoles';
import { useAccountCreateContext } from 'modules/account/create/AccountCreateContext';
import { getAvailableRolesForUser, type OrgTypeApi } from 'modules/account/roleFilters';
import { isClientOrgType } from 'enum/combobox';

const StepThree: FC = () => {
  const {
    state,
    isCQQL,
    isCreatePending,
    clients,
    goPreviousStep,
    handleCreateUser,
    setCurrentSelected,
    setRolesToAssign,
  } = useAccountCreateContext();

  const { userInfo, unitAddress, currentSelected, rolesToAssign: rolesToAssignData } = state;
  const currentSelectedRole = { data: currentSelected, onSet: setCurrentSelected };
  const rolesToAssign = { data: rolesToAssignData, onSet: setRolesToAssign };
  const {
    customersSource,
    rolesAssignment: { forGovAgencyBusiness, forSystemRoles },
  } = rolesToAssignData;

  const [availableRoles, setRoles] = useState<Partial<Roles>>({
    businessRoles: [],
    adminRoles: [],
    systemRoles: [],
  });
  const { formatMessage } = useIntl();
  const { data: availableRolesData } = useGetAvailableRoles();
  const { data: hvsInfo } = useGetHvsInfo();
  const loggedInUserRoles = useUserRoles();

  const handleSelectRole = (rolesType: string, role: CommonRole) =>
    currentSelectedRole.onSet({ rolesType, role });

  const selectACustomer = (selectedClient: Client, isChecked: boolean) => {
    if (!currentSelectedRole.data) return;
    const { rolesType } = currentSelectedRole.data;
    const roleId = currentSelectedRole.data.role['id'];
    const clientOperationCode = selectedClient.operationCode;

    if (rolesType === 'businessRoles' && isCQQL) {
      const roleToEdit = findByKey(forGovAgencyBusiness, roleId);
      rolesToAssign.onSet((old) =>
        roleToEdit
          ? updateIncludedClientIds(old, roleToEdit, isChecked, clientOperationCode)
          : addNewGovRole(old, {
              roleId,
              orgId: unitAddress!.operationCode,
              includedClientIds: [clientOperationCode],
              includedClientByBoundary: false,
            })
      );
      return;
    }

    const targetRoleType = convertRoles(rolesType);
    rolesToAssign.onSet((old) => {
      const otherRoles = { ...old };
      const customers = otherRoles.rolesAssignment[targetRoleType];
      otherRoles.rolesAssignment[targetRoleType] = isChecked
        ? addClient(customers, { roleId, orgId: clientOperationCode })
        : removeClient(customers, { roleId, orgId: clientOperationCode });
      return otherRoles;
    });
  };

  const selectAllCustomer = (checked: boolean) => {
    if (!currentSelectedRole.data) return;
    const { rolesType } = currentSelectedRole.data;
    const roleId = currentSelectedRole.data.role['id'];

    rolesToAssign.onSet((old) => {
      if (rolesType !== 'businessRoles')
        return setCustomersByRoleType(rolesType, old, checked, roleId);
      if (isCQQL) {
        const roleToEdit = findByKey(forGovAgencyBusiness, roleId);
        if (roleToEdit) return addAllOrRemoveAllClientsId(old, roleToEdit, checked);
        if (!checked) return old;
        return addAllClientsForNewGov(old, { roleId, orgId: unitAddress!.operationCode });
      }
      return setCustomersByRoleType('forClientBusiness', old, checked, roleId);
    });
  };

  const handleSelectSystemRole = (roleId: string, add: boolean) => {
    const orgId = hvsInfo?.company?.operationCode ?? '';
    rolesToAssign.onSet((old) => updateSystemRoles(old, add, { roleId, orgId }));
  };

  const createSharedData = (old: RolesToAssign, roleId: string) => {
    const newRoles = { ...old };
    const { forGovAgencyBusiness: govs } = newRoles.rolesAssignment;
    const others = govs.filter((gov) => gov.roleId !== roleId);
    const govRole = govs.find((gov) => gov.roleId === roleId);
    return { newRoles, others, govRole };
  };

  const byLocality = useMemo(() => {
    if (!unitAddress) return [];
    return customersSource.reduce<string[]>(
      (acc, customer) =>
        customer.wardName !== unitAddress.ward ? acc : [...acc, customer.operationCode],
      []
    );
  }, [customersSource, unitAddress]);

  const addGovRole = () => {
    const roleId = currentSelectedRole!.data!.role.id;
    rolesToAssign.onSet((old) => {
      const { newRoles, others, govRole } = createSharedData(old, roleId);
      if (!govRole)
        return addNewGovRole(newRoles, {
          roleId,
          orgId: unitAddress!.operationCode,
          includedClientIds: byLocality,
          includedClientByBoundary: true,
        });
      newRoles.rolesAssignment.forGovAgencyBusiness = [
        ...others,
        { ...govRole, includedClientIds: byLocality, includedClientByBoundary: true },
      ];
      return newRoles;
    });
  };

  const unSwitchCustomerLocality = () => {
    const roleId = currentSelectedRole!.data!.role.id;
    rolesToAssign.onSet((old) => {
      const { newRoles, others, govRole } = createSharedData(old, roleId);
      if (!govRole) return newRoles;
      newRoles.rolesAssignment.forGovAgencyBusiness = [
        ...others,
        { ...govRole, includedClientIds: byLocality, includedClientByBoundary: false },
      ];
      return newRoles;
    });
  };

  const handleCustomerLocalitySelect = (shouldAddNew: boolean) => {
    if (!currentSelectedRole.data) return;
    shouldAddNew ? addGovRole() : unSwitchCustomerLocality();
  };

  const utils: UtilFunctions = { selectACustomer, selectAllCustomer };
  const isBothBusinessRoleAndCQQL = useMemo(
    () => isCQQL && currentSelectedRole.data?.rolesType === 'businessRoles',
    [isCQQL, currentSelectedRole.data]
  );
  // Xét theo LOẠI ĐƠN VỊ đã chọn ở bước 2, KHÔNG cắt tiền tố mã CĐS: mã khách hàng giờ là
  // `HSKD-*` (KIOS đổi bộ mã) nên cách cũ so với 'KH' luôn ra false, khiến danh sách khách hàng ở
  // tab Gán vai trò không bị bó về đúng đơn vị đã chọn.
  const isClient = useMemo(
    () => isClientOrgType(userInfo.organizationTypeId),
    [userInfo.organizationTypeId]
  );
  const orgType = (convertOrgTypeToOriginal(userInfo.organizationTypeId) as OrgTypeApi) || 'client';
  const availableRolesFiltered = getAvailableRolesForUser(
    loggedInUserRoles,
    orgType,
    availableRoles ?? {},
    userInfo.jobTitleId // lọc vai trò nghiệp vụ theo chức vụ tài khoản đang tạo (client)
  );

  useEffect(() => {
    if (!availableRolesData)
      return setRoles({ businessRoles: [], adminRoles: [], systemRoles: [] });
    setRoles(formatRolesData(isCQQL, availableRolesData));
  }, [availableRolesData, isCQQL]);

  useEffect(() => {
    rolesToAssign.onSet((old) => ({ ...old, customersSource: clients || [] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clients]);

  if (!unitAddress) return null;

  return (
    <>
      <Grid container spacing={2} sx={{ paddingLeft: '16px' }}>
        <Grid item xs={6} sx={{ minHeight: '60dvh' }}>
          <FilterButtons />
          <Box sx={{ marginTop: '32px', marginRight: '16px', paddingBottom: '16px' }}>
            {Object.keys(availableRolesFiltered).map((type: keyof Roles) => {
              const rolesInGroup = (availableRolesFiltered[type] || []) as CommonRole[];
              if (!rolesInGroup.length) return null;
              const isSystemRole = type === 'systemRoles';
              return (
                <Accordion key={type}>
                  <RoleCategory label={`account.roles.${type}`} />
                  {rolesInGroup.map((role) => {
                    const isStaticRoleType = role['parentId'] === null;
                    const isCurrentSelected =
                      currentSelectedRole.data?.role['name'] === role['name'];
                    return (
                      <AccordionDetails
                        key={role['id']}
                        sx={{
                          border: '1px solid',
                          borderColor: isCurrentSelected ? 'primary.main' : 'divider',
                          margin: 1,
                          borderRadius: '12px',
                        }}
                        onClick={() => handleSelectRole(`${type}`, role)}
                      >
                        {isStaticRoleType &&
                          (isSystemRole ? (
                            <SystemAdminRoleType
                              role={role}
                              onSelectSystemRole={handleSelectSystemRole}
                              isChecked={!!forSystemRoles.find(({ roleId }) => roleId === role.id)}
                            />
                          ) : (
                            <StaticRoleType
                              role={role}
                              rolesToAssign={rolesToAssign.data}
                              isCCQL={isCQQL}
                              isCurrentSelected={isCurrentSelected}
                              rolesType={`${type}`}
                              unitAddress={unitAddress}
                            />
                          ))}
                      </AccordionDetails>
                    );
                  })}
                </Accordion>
              );
            })}
          </Box>
        </Grid>
        <Grid item xs={6} sx={{ minHeight: '60dvh', maxHeight: '70vh', overflow: 'scroll' }}>
          {shouldShowCustomerList(currentSelectedRole.data) && (
            <>
              <CustomersListHeading label={formatMessage({ id: 'account.customersList' })} />
              {isBothBusinessRoleAndCQQL ? (
                <CustomersForCQQL
                  unitAddress={unitAddress}
                  onSelectCustomersByLocality={handleCustomerLocalitySelect}
                  isCQQL={isCQQL}
                  utilFunctions={utils}
                  rolesToAssign={rolesToAssign.data}
                  currentSelectedRole={currentSelectedRole.data}
                />
              ) : (
                <FullCustomersList
                  customers={
                    isClient
                      ? customersSource.filter(
                          (c) => c.operationCode === unitAddress!.operationCode
                        )
                      : customersSource
                  }
                  isCQQL={isCQQL}
                  utilFunctions={utils}
                  rolesToAssign={rolesToAssign.data}
                  currentSelectedRole={currentSelectedRole.data}
                />
              )}
            </>
          )}
        </Grid>
      </Grid>
      <Box sx={{ display: 'flex', justifyContent: 'end' }}>
        <Button
          variant="outlined"
          startIcon={<Icon icon={Undo2} size={16} />}
          sx={{ marginRight: 2 }}
          onClick={goPreviousStep}
        >
          {formatMessage({ id: 'back' })}
        </Button>
        <Button
          variant="contained"
          startIcon={<Icon icon={Save} size={16} />}
          onClick={handleCreateUser}
          disabled={isCreatePending}
        >
          {formatMessage({ id: 'save' })}
        </Button>
      </Box>
    </>
  );
};

export default StepThree;
